import React, { FC, ReactNode, useCallback, useMemo, useState } from 'react'
import { BetterSelect } from '@pushly/aqe/lib/components/better-select/better-select'
// import Select, { type SelectProps, type DefaultOptionType } from 'antd/lib/select'
import { Loading3QuartersOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'
import { getClassNames } from '../../../../_utils/classnames'
import { OPERATORS_MAP } from '../constants'
import '../style/insights-filter-builder.scss'
import { Select, SelectProps } from '@pushly/aqe/lib/components'

const DOMAIN_ID_FIELD = 'DOMAIN_ID'

type OperatorOption = {
    value: string
    label: string
}

type FilterValue = {
    $mode: string
    field: string
    operator: string
    value: number[]
}

type SelectFilterBuilder = {
    // display mode props
    descriptor: string
    allSelectedDisplay?: string
    maxDisplayValues?: number

    // control props
    value: FilterValue
    onChange: (change: Partial<FilterValue>) => void

    // operator props
    operatorOptions: OperatorOption[]

    // value props
    /**
     * Filter Value value select properties
     */
    valueOptions: SelectProps['options']
    valueSelectProps?: Omit<SelectProps, 'options' | 'value'>
}

export const SelectFilterBuilder: FC<SelectFilterBuilder> = ({
    descriptor,
    allSelectedDisplay,
    maxDisplayValues,
    value,
    onChange,
    operatorOptions,
    valueOptions = [],
    valueSelectProps,
}) => {
    const [loading, setLoading] = useState<boolean>(false)
    const [selectedValues, setSelectedValues] = useState<any[]>(value.value ?? [])
    const [operator, setOperator] = useState<string>(value.operator ?? 'in')

    const currentValueDisplay = useMemo((): string => {
        let display: string = ''
        const selectedValuesDisplayArray = selectedValues.map((s) => s.label)

        if (selectedValuesDisplayArray.length < 1 || selectedValuesDisplayArray.length === valueOptions.length) {
            display = allSelectedDisplay ?? `all ${descriptor}s`
        } else {
            if (selectedValuesDisplayArray.length <= (maxDisplayValues ?? 2)) {
                display = selectedValuesDisplayArray.join(' or ')
            } else {
                const lastValue = selectedValuesDisplayArray.pop()
                display =
                    (maxDisplayValues
                        ? ` any of ${selectedValuesDisplayArray.length} ${descriptor}s`
                        : selectedValuesDisplayArray.join(', ') + ',') + ` or ${lastValue}`
            }
        }

        return display
    }, [selectedValues, valueOptions, value.$mode])

    const handleChange = useCallback(
        <Key extends keyof FilterValue>(change: Record<Key, FilterValue[Key]>) => {
            onChange({
                $mode: value.$mode,
                field: DOMAIN_ID_FIELD,
                value: selectedValues.map((v) => v.value),
                operator,
                ...change,
            })
        },
        [selectedValues, value],
    )

    return (
        <div className="select-filter">
            <div className={getClassNames('filter', 'insights-filter-input')}>
                <div className={getClassNames('filter-wrapper')}>
                    <div className={getClassNames('filter-display')}>
                        <div className="filter-op">{OPERATORS_MAP[operator.toUpperCase()]?.label}</div>

                        <div className="filter-val">
                            <div id="borderDisplay"></div>
                            {loading ? (
                                <Loading3QuartersOutlined
                                    size={1}
                                    spin={true}
                                    style={{ display: 'inline-flex', marginTop: '2px' }}
                                />
                            ) : (
                                <span>{currentValueDisplay}</span>
                            )}
                        </div>
                    </div>

                    <div className={getClassNames('filter-editor')}>
                        <div className="filter-op">
                            <BetterSelect
                                size="small"
                                disableSearch={true}
                                dropdownClassName="filter-op-dropdown"
                                options={operatorOptions}
                                value={[operator]}
                                onChange={(nextOperator: string) => {
                                    setOperator(nextOperator)
                                    handleChange({ operator: nextOperator })
                                }}
                            />
                        </div>

                        <div className="filter-val">
                            <div id="borderEdit"></div>
                            <Select
                                {...valueSelectProps}
                                mode={valueSelectProps?.mode}
                                size="small"
                                className="sw-v2-select-filter"
                                options={valueOptions}
                                optionFilterProp="label"
                                onChange={(selections) => {
                                    setSelectedValues(selections)
                                    handleChange({ value: selections.map((s) => s.value) })
                                }}
                                placeholder={
                                    loading ? (
                                        <Loading3QuartersOutlined
                                            size={1}
                                            spin={true}
                                            style={{ display: 'inline-flex', marginTop: '2px' }}
                                        />
                                    ) : (
                                        valueSelectProps?.placeholder ?? `Select ${descriptor}s`
                                    )
                                }
                                labelInValue={true}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
